import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2, KeyRound, Mail, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input, PasswordInput } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { PasswordRequirements } from '../components/PasswordRequirements'
import { confirmPasswordReset, requestPasswordReset } from '../api/auth'
import { translateCognitoError } from '../lib/cognitoErrors'
import { isPasswordValid } from '../lib/passwordPolicy'
import { saveLastUsername, loadLastUsername } from '../lib/usernameStorage'
import { cn } from '@/lib/utils'

type Step = 'request' | 'confirm' | 'success'

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('request')
  const [username, setUsername] = useState(() => loadLastUsername())
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onRequest(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await requestPasswordReset(username)
      setStep('confirm')
    } catch (err) {
      setError(translateCognitoError(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!isPasswordValid(newPassword)) {
      setError('La password non rispetta i requisiti minimi.')
      return
    }
    setSubmitting(true)
    try {
      await confirmPasswordReset(username, code, newPassword)
      saveLastUsername(username.trim())
      setStep('success')
    } catch (err) {
      setError(translateCognitoError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout>
      <div className="w-full">
        <div className="mb-6 flex flex-col items-center text-center">
          <StepDots step={step} />
          <StepIcon step={step} />
          <h1 className="text-2xl font-bold text-foreground">
            {step === 'request' && 'Reimposta password'}
            {step === 'confirm' && 'Inserisci il codice'}
            {step === 'success' && 'Tutto fatto'}
          </h1>
          <p className="mt-1.5 max-w-xs text-sm text-muted-foreground">
            {step === 'request' &&
              'Inserisci il tuo nome utente. Ti invieremo un codice via email (o via SMS in alcuni casi). Controlla anche lo spam.'}
            {step === 'confirm' && (
              <>
                Inserisci il codice ricevuto e scegli una nuova password sicura
                {username.trim() && (
                  <>
                    {' '}per <span className="font-semibold text-foreground">{username.trim()}</span>
                  </>
                )}
                .
              </>
            )}
            {step === 'success' && 'Password aggiornata. Ora puoi accedere con la nuova password.'}
          </p>
        </div>

        {step === 'request' && (
          <form className="flex flex-col gap-4" onSubmit={onRequest}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fp-username">Nome utente</Label>
              <Input
                id="fp-username"
                placeholder="Il tuo nome utente"
                autoComplete="username"
                required
                leadingIcon={<User className="h-4 w-4" />}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={submitting} className="mt-2">
              {submitting ? <Spinner className="text-secondary-foreground" /> : 'Invia codice'}
            </Button>
            <div className="mt-1 text-center text-sm">
              <Link
                to="/login"
                className="font-semibold text-secondary underline-offset-2 hover:underline"
              >
                Torna al login
              </Link>
            </div>
          </form>
        )}

        {step === 'confirm' && (
          <form className="flex flex-col gap-4" onSubmit={onConfirm}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fp-code">Codice ricevuto</Label>
              <Input
                id="fp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6 cifre"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fp-pwd">Nuova password</Label>
              <PasswordInput
                id="fp-pwd"
                placeholder="Almeno 8 caratteri"
                autoComplete="new-password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <PasswordRequirements password={newPassword} />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={submitting} className="mt-2">
              {submitting ? <Spinner className="text-secondary-foreground" /> : 'Conferma'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setStep('request')
                setError(null)
              }}
              className="text-muted-foreground"
            >
              Richiedi un nuovo codice
            </Button>
          </form>
        )}

        {step === 'success' && (
          <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
            Vai al login
          </Button>
        )}
      </div>
    </AuthLayout>
  )
}

function StepDots({ step }: { step: Step }) {
  const order: Step[] = ['request', 'confirm', 'success']
  const idx = order.indexOf(step)
  return (
    <div className="mb-3 flex gap-1.5">
      {order.slice(0, 2).map((s, i) => (
        <span
          key={s}
          className={cn(
            'h-1.5 w-8 rounded-full transition-colors',
            i <= idx ? 'bg-secondary' : 'bg-muted',
          )}
        />
      ))}
    </div>
  )
}

function StepIcon({ step }: { step: Step }) {
  const cls = 'mb-3 h-12 w-12'
  if (step === 'request') return <Mail className={cn(cls, 'text-secondary')} />
  if (step === 'confirm') return <KeyRound className={cn(cls, 'text-secondary')} />
  return <CheckCircle2 className={cn(cls, 'text-success')} />
}
